import { takeEvery, put, call, all, select, takeLatest, fork } from 'redux-saga/effects';
import { serviceRequest, loading, toast, modal, inventory } from '../types';
import scogo_apis from '../scogo_apis';
import iceberg_apis from '../iceberg_apis';
import { generateCSV } from '../utils/CreateCsv';
import { actualValueForDropdown, uploadFileToS3WithSignedUrlNew } from '../utils/common';
import { RegionsType, TicketAgentRole, TicketMilestoneEvents, todayStatus } from '../pages/Ticket/TicketController';

export function* getServiceRequestList(action) {
    try {
        const { limit, page, query, payload, source, give_actions = true, tab, callCountApi = true, append = false } = action.payload;
        if (tab === todayStatus.path) {
            payload.status = 'OPEN,IN_PROGRESS';
        }
        yield put({ type: serviceRequest.SET_SERVICE_REQUEST_FILTER_PAYLOAD, payload: { limit, page, query, payload, source, give_actions } });
        const queryParams = { limit, page, ...payload, give_actions };
        if (query) queryParams.q = query.trim();

        // Deleting queryParams which are not to send
        Object.keys(TicketAgentRole).forEach(key => {
            delete queryParams[key];
        })
        Object.keys(RegionsType).forEach(key => {
            delete queryParams[key];
        })

        yield put({ type: loading.SET_SERVICE_REQUEST_LIST_LOADING, payload: true });
        const response = yield call(scogo_apis.serviceRequest.getServiceRequest, {}, {}, queryParams);
        if (response.status === 200) {
            const updateParam = queryParams;
            delete updateParam.give_actions;
            delete updateParam.limit;
            delete updateParam.page;

            try {
                const serviceRequestList = yield select(state => state.serviceRequest.serviceRequestList);
                const cachedCount = serviceRequestList && serviceRequestList.metadata && serviceRequestList.metadata.total_count;
                const hasCachedCount = typeof cachedCount === 'number' && !isNaN(cachedCount);
                // on a refresh / direct load of a page other than the first one there is no count in the store to reuse,
                // so the count apis have to be called for that page as well
                if (callCountApi && (page === 1 || !hasCachedCount)) {
                    yield put({ type: serviceRequest.GET_SERVICE_REQUEST_COUNT_V2, payload: queryParams });
                    yield put({ type: serviceRequest.GET_SERVICE_REQUEST_COUNT_BY_STATUS_REQUEST, payload: queryParams });
                } else if (hasCachedCount && response.data.metadata) {
                    response.data.metadata.total_count = cachedCount;
                }
            } catch (err) {
                console.log('err:', err)
            }

            if (source === 'ticket_chat') {
                yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_IDS_SUCCESS, payload: response.data.data });
            } else {
                yield put({ type: serviceRequest.GET_SERVICE_REQUEST_LIST_SUCCESS, payload: response.data, append: append && page > 1 });
                let ids = response.data.data.map(item => item.id);
                if (ids.length > 0) {
                    yield put({ type: serviceRequest.GET_EXTRA_DETAILS_BY_IDS_REQUEST, payload: { ids: ids.join(',') } });
                }
            }
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.SET_SERVICE_REQUEST_LIST_LOADING, payload: false });
    }
}


export function* createServiceRequest(action) {
    const { payload, formName, onSuccess, onError, refreshAsset } = action.payload;
    const isCreatIssueCategory = payload.issue_category && payload.issue_category.value === 'create_this_issue';
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    if (isCreatIssueCategory) {
        const issueCategoryResponse = yield call(scogo_apis.admin.createRemark, {}, {}, {}, {
            master_remark: payload.issue_category.label,
            is_default: true,
            category: "ISSUE_CATEGORY",
            project_id: payload.project_id,
            org_id: payload.org_id
        });
        if (issueCategoryResponse.status === 201 && issueCategoryResponse.data.data.master_remark) {
            payload.issue_category = issueCategoryResponse.data.data.master_remark;
        }
    } else {
        payload.issue_category = actualValueForDropdown(payload.issue_category);
    }

    if (payload.custom_fields && Array.isArray(payload.custom_fields)) {
        for (const field of payload.custom_fields) {
            if (field.type === 'FILE' && field.value instanceof FileList) {
                const files = Array.from(field.value);
                const requestBody = {
                    files: files.map(file => ({
                        name: file.name,
                        mime_type: file.type,
                        size: file.size
                    })),
                    purpose: 'TICKET_CUSTOM',
                    additionalData: { create: true }
                };
                const signedUrlRes = yield call(scogo_apis.user.postImageUrl, {}, {}, {}, requestBody);
                if (signedUrlRes.status !== 201) {
                    field.value = [];
                    continue;
                }
                const postUrls = signedUrlRes?.data?.data?.postUrls || [];
                if (signedUrlRes?.data?.data?.ticketId) {
                    payload.id = signedUrlRes?.data?.data?.ticketId;
                }
                yield Promise.all(
                    files.map(async (file) => {
                        const f = postUrls.find(({ name }) => name === file.name);
                        if (!f) {
                            console.error(`No signed URL found for file: ${file.name}`);
                            return;
                        }
                        const body = {
                            'Content-Type': f.mimeType,
                            'key': f.payload.key,
                            ...(f.payload.acl ? { 'acl': f.payload.acl } : {}),
                            'X-Amz-Date': f.payload.date,
                            'X-Amz-Algorithm': f.payload.algorithm,
                            'x-amz-meta-uuid': f.payload.uuid,
                            'X-Amz-Credential': f.payload.credential,
                            'Policy': f.payload.policy,
                            'X-Amz-Signature': f.payload.signature,
                        };
                        try {
                            await uploadFileToS3WithSignedUrlNew(f.postUrl, file, body);
                        } catch (uploadError) {
                            console.error(`Error uploading file ${file.name}:`, uploadError);
                        }
                    })
                );
                field.value = postUrls.map(f => f.fileUrl).filter(Boolean);
            }
            delete field.type;
        }
    }

    try {
        if (payload.create_end_user) {
            const endUserResponse = yield call(scogo_apis.org.postEntities, {}, { orgId: payload.org_id }, {}, { name: payload.create_end_user, type: 'END_USER' });
            delete payload.create_end_user;
            if (endUserResponse.status === 201) {
                payload.end_user_id = endUserResponse.data.data.id
            }
        }
        const response = yield call(scogo_apis.serviceRequest.createServiceRequest, {}, {}, {}, payload);

        if (response.status === 201) {
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
            if (onSuccess) onSuccess();
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: 'Ticket Created Successfully' } });
            if (refreshAsset) yield put({ type: inventory.REFRESH_ASSETS_REQUEST });
        } else {
            const message = response.data.messages?.[0] || 'Something went wrong';
            yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message } });
            if (onError) onError(message);
        }
    } catch (error) {
        console.error('Error creating service request:', error);
        const message = error?.response?.data?.messages?.[0] || 'Something went wrong';
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message } });
        if (onError) onError(message);
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* updateServiceRequest(action) {
    const { ticketId, payload, formName, onSuccess } = action.payload;
    try {
        if (payload.custom_fields && Array.isArray(payload.custom_fields)) {
            for (const field of payload.custom_fields) {
                if (field.type === 'FILE' && field.value instanceof FileList) {
                    const files = Array.from(field.value);
                    const requestBody = {
                        files: files.map(file => ({
                            name: file.name,
                            mime_type: file.type,
                            size: file.size
                        })),
                        purpose: 'TICKET_CUSTOM',
                        ticket_id: ticketId
                    };
                    const signedUrlRes = yield call(scogo_apis.user.postImageUrl, {}, {}, {}, requestBody);
                    if (signedUrlRes.status !== 201) {
                        field.value = [];
                        continue;
                    }
                    const postUrls = signedUrlRes?.data?.data?.postUrls || [];
                    yield Promise.all(
                        files.map(async (file) => {
                            const f = postUrls.find(({ name }) => name === file.name);
                            if (!f) {
                                console.error(`No signed URL found for file: ${file.name}`);
                                return;
                            }
                            const body = {
                                'Content-Type': f.mimeType,
                                'key': f.payload.key,
                                ...(f.payload.acl ? { 'acl': f.payload.acl } : {}),
                                'X-Amz-Date': f.payload.date,
                                'X-Amz-Algorithm': f.payload.algorithm,
                                'x-amz-meta-uuid': f.payload.uuid,
                                'X-Amz-Credential': f.payload.credential,
                                'Policy': f.payload.policy,
                                'X-Amz-Signature': f.payload.signature,
                            };
                            try {
                                await uploadFileToS3WithSignedUrlNew(f.postUrl, file, body);
                            } catch (uploadError) {
                                console.error(`Error uploading file ${file.name}:`, uploadError);
                            }
                        })
                    );
                    field.value = postUrls.map(f => f.fileUrl).filter(Boolean);
                }
                delete field.type;
            }
        }

        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.updateServiceRequest, {}, { ticketId }, {}, payload);
        if (response.status === 200) {
            if (response?.data?.data?.existing_ticket) {
                let message = `Ticket ${response.data.data.existing_ticket.ticket_number} already exists ${response?.data?.data?.existing_ticket?.location?.external_id ? `at ${response.data.data.existing_ticket.location.external_id}` : ''}, hard closing ${response.data.data.ticket_number}`
                yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message } })
            }
            yield put({ type: serviceRequest.UPDATE_SERVICE_REQUEST_SUCCESS, payload: response.data });
            onSuccess();
        } else {
            yield put({ type: serviceRequest.UPDATE_SERVICE_REQUEST_FAILURE, payload: response.data });
        }
    } catch (error) {
        yield put({ type: serviceRequest.UPDATE_SERVICE_REQUEST_FAILURE, payload: error });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* deleteServiceRequest(action) {
    const { ticketId, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.deleteServiceRequest, {}, { ticketId }, {}, {});
        if (response.status === 200) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: 'Ticket Deleted Successfully' } });
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        } else {
            yield put({ type: serviceRequest.DELETE_SERVICE_REQUEST_FAILURE, payload: response.data });
        }
    } catch (error) {
        yield put({ type: serviceRequest.DELETE_SERVICE_REQUEST_FAILURE, payload: error });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* getServiceRequestFilterList(action) {
    const { filter_labels, project_ids, q, formName } = action.payload;
    try {
        const query = { filter_labels };
        if (project_ids && project_ids.length > 0) query.project_ids = project_ids;
        if (q) query.q = q;
        if (!q) {
            yield put({ type: loading.TICKET_FILTER_LIST_LOADING, payload: true })
        } else if (q && formName) {
            yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        }
        const response = yield call(scogo_apis.serviceRequest.getServiceRequestFilterList, {}, {}, query);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_FILTER_LIST_SUCCESS, payload: { data: response.data.data, q } });
        }
    } catch (error) {
        yield put({ type: serviceRequest.GET_SERVICE_REQUEST_FILTER_LIST_FAILURE, payload: error });
    }
    if (!q) yield put({ type: loading.TICKET_FILTER_LIST_LOADING, payload: false });
    else if (q && formName) {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* getServiceRequestCount() {
    try {
        const response = yield call(scogo_apis.serviceRequest.getServiceRequestCount);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_COUNT_SUCCESS, payload: response.data.data });
        }
    } catch (error) {
        yield put({ type: serviceRequest.GET_SERVICE_REQUEST_COUNT_FAILURE, payload: error });
    }
}

export function* refreshServiceRequestTable(action) {
    try {
        const serviceRequestFilterPayload = yield select(state => state.serviceRequest.serviceRequestFilterPayload);
        yield put({ type: serviceRequest.GET_SERVICE_REQUEST_LIST_REQUEST, payload: serviceRequestFilterPayload });
    } catch (error) {
        console.log('error:', error)
    }
}

export function* holdOrResumeServiceRequest(action) {
    const { payload, formName } = action.payload;

    let ticketId = payload.ticketId
    let state = payload.state
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.holdOrResumeServiceRequest, {}, { ticketId, state }, {}, payload.remark);
        if (response.status === 200) {
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: 'Ticket Updated Successfully' } });
            yield put({ type: modal.CLOSE_MODAL });
        }
    } catch (error) {
        yield put({ type: serviceRequest.HOLD_OR_RESUME_SERVICE_REQUEST_FAILURE, payload: error });
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });

    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* hardCloseServiceRequest(action) {
    const { ticketId, remark, closed_at, formName, milestone_ids } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.hardCloseServiceRequest, {}, { ticketId }, {}, { remark, closed_at, milestone_ids });
        if (response.status === 200) {
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Tickets Updated Successfully' } });
            yield put({ type: modal.CLOSE_MODAL });
        }
    } catch (error) {
        yield put({ type: serviceRequest.HARD_CLOSE_SERVICE_REQUEST_FAILURE, payload: error });
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });

    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}
export function* bulkTicketCreate(action) {
    const { formName, type, url, project_id, org_id, site_type, warehouse_id } = action.payload;

    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const payload = {
            type,
            url,
            project_id,
            org_id,
            warehouse_id
        };
        if (site_type) {
            payload.site_type = site_type;
        }
        const response = yield call(scogo_apis.serviceRequest.bulkTicketCreate, {}, {}, {}, payload);
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response?.data?.message } });
            yield put({ type: modal.CLOSE_MODAL });
        }
    } catch (error) {

        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } })
    }
}
export function* getEligibleAgents(action) {
    const { ticketId, limit, page, query, payload } = action.payload;
    try {
        const queryParams = { limit, page };
        if (query) queryParams.q = query;
        yield put({ type: loading.SET_SERVICE_REQUEST_AGENTS_LIST_LOADING, payload: true });
        const response = yield call(scogo_apis.serviceRequest.getEligibleAgents, {}, { ticketId }, queryParams, payload);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_ELIGIBLE_AGENTS_SUCCESS, payload: response.data.data });
        }
    } catch (error) {
        console.log('error:', error)
    } finally {
        yield put({ type: loading.SET_SERVICE_REQUEST_AGENTS_LIST_LOADING, payload: false });
    }
}

export function* acceptTicketMilestone(action) {
    const { ticketId, milestoneIds, payload, formName, source, callTicketDetails } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });

        for (const milestoneId of milestoneIds) {
            const response = yield call(scogo_apis.milestones.accept, {}, { ticketId, milestoneId }, {}, payload);
            if (response.status !== 200) {
                throw new Error(response?.data?.messages?.[0] || 'Failed to accept milestone');
            }
        }

        const promises = [
            put({ type: toast.SEND_TOAST, payload: { status: 'success', message: source === 'assign_ticket_to_user' ? 'Tickets Assigned Successfully' : 'Milestones Accepted Successfully' } }),
            put({ type: modal.CLOSE_MODAL })
        ];

        if (callTicketDetails) {
            promises.push(put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId } }));
        }

        if (source === 'new_ticket') {
            promises.push(put({ type: serviceRequest.TRIGGERED_SERVICE_REQUEST_REQUEST, payload: { limit: 10, page: 1 } }));
        } else {
            promises.push(put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }));
        }
        yield all(promises);
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
};

export function* bulkTicketExampleFile(action) {
    const { type } = action.payload
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkExampleFile, {}, {}, { type }, {});

        if (response.status == 200) {
            generateCSV(response.data);
        }
    } catch (error) {
        console.log('error:', error)
    }
}

export function* assignAgentsToTicketMilestone(action) {
    const { ticketId, milestoneId, payload, formName, callTicketDetails } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.assign, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Agents Assigned Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
            if (callTicketDetails) {
                yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId } });
            }
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }

}

export function* broadcastTicketMilestone(action) {
    const { ticketId, milestoneId, payload, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.broadcast, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 200) {
            yield all([
                // put({ type: serviceRequest.BROADCAST_SUCCESS, payload: response.data }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Broadcast Sent Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
        }
    } catch (error) {
        console.log('error:', error)
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* siteCompleteTicketMilestone(action) {
    const { ticketId, milestoneId, formName, refresh = true } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.siteComplete, {}, { ticketId, milestoneId }, {}, {});
        if (response.status === 200) {
            yield all([
                put({ type: serviceRequest.SITE_COMPLETE_SUCCESS, payload: { milestone: response.data.data, ticketId } }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Site Completed Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
            ]);
            if (refresh) yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* executionDateTicketMilestone(action) {
    const { ticketId, milestoneId, formName, payload } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.executionDate, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 200) {
            yield all([
                // put({ type: serviceRequest.EXECUTION_DATE_SUCCESS, payload: response.data }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Execution Date Set Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
        }
    } catch (error) {
        console.log('error:', error)
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* reAssignAgentToServiceReuest(action) {
    const { ticketId, milestoneId, payload, formName, callTicketDetails } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.reAssign, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Agents Reassigned Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
            if (callTicketDetails) {
                yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId } });
            }
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* assignHelpdesk(action) {
    const { payload, formName, ticketId } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.assignHelpdesk, {}, { ticketId }, {}, payload);
        if (response.status === 201) {
            yield all([
                // put({ type: serviceRequest.ASSIGN_HELPDESK_SUCCESS, payload: response.data }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Helpdesk Assigned Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* getServiceRequestById(action) {
    const { ticketId, callDetail, call_after_site_update } = action.payload;
    try {
        const newLoggedUserData = yield select(state => state.auth.newLoggedUserData);
        const isLoginUserIsSP = newLoggedUserData.user.roles.find((elem) => elem.label === 'SP');
        if (call_after_site_update) {
            yield put({ type: loading.GET_SERVICE_REQUEST_BY_ID_AFTER_SITE_UPDATE_LOADING, payload: true });
        } else {
            yield put({ type: loading.GET_SERVICE_REQUEST_BY_ID_LOADING, payload: true });
        }
        const response = yield call(scogo_apis.serviceRequest.getById, {}, { ticketId }, { give_actions: true }, {});
        let details;
        if (callDetail && response.status === 200) {
            details = yield call(scogo_apis.serviceRequest.extraDetailsByIds, {}, {}, { ids: ticketId }, {});
        }
        if (response.status === 200) {
            const data = response.data.data;
            if (details && details.status === 200) {
                let { assets, milestones, remarks, trackingDetails, ticket_payment_ratios, milestoneWiseAdditionalApprovedAmount } = details.data;
                data.remarks = remarks.filter(remark => remark.ticket_id === ticketId);


                const filterCurrentMilestones = milestones.filter(milestone => milestone.ticket_id === ticketId);

                // Milestone Filter Condition
                if (isLoginUserIsSP && filterCurrentMilestones?.length > 0) {
                    const findMilestonesOfCurrentUserAccepted = data.agents.filter((ag) => ag.agent.id === newLoggedUserData.user.id && ag.role === 'SP').map((elem) => elem.milestone_id) || [];
                    data.milestones = filterCurrentMilestones.filter((mile) => findMilestonesOfCurrentUserAccepted.includes(mile.id));
                } else {
                    data.milestones = filterCurrentMilestones;
                }

                data.assets = assets.filter(asset => asset.ticket_id === ticketId);
                data.tracking_details = trackingDetails.find(track => track.ticket_id === ticketId);

                const distanceTravelled = data.remarks.filter(remark => remark.action === TicketMilestoneEvents.MILESTONE_ENGINEER_AT_SITE);
                if (distanceTravelled && distanceTravelled.length > 0) {
                    const uniqueDistanceTravelled = distanceTravelled.filter((elem, index, arr) => {
                        const elemDate = new Date(elem.updated_at).toDateString();
                        return arr.findIndex(item =>
                            item.milestone_id === elem.milestone_id &&
                            new Date(item.updated_at).toDateString() === elemDate
                        ) === index;
                    });
                    let dt = 0;
                    uniqueDistanceTravelled.forEach((elem) => {
                        if (elem?.payload?.dist_travelled_meter) {
                            dt += elem.payload.dist_travelled_meter
                        }
                    });
                    data.dist_travelled_meter = dt;
                }


                let mwaaa = [];
                if (milestoneWiseAdditionalApprovedAmount.length > 0) {
                    mwaaa = milestoneWiseAdditionalApprovedAmount.filter((elem) => elem.ticket_id === data.id);
                }
                data.milestone_wise_additional_approved_amount = mwaaa;

                if (data?.agents?.length > 0 && ticket_payment_ratios?.length > 0) {
                    data.agents = data.agents.map((ag) => {
                        if (![TicketAgentRole.CLUSTER, TicketAgentRole.PSP, TicketAgentRole.SP, TicketAgentRole.WAREHOUSE_OWNER, TicketAgentRole.SUPERVISOR].includes(ag.role)) return ag;

                        const findCurrentAgent = ticket_payment_ratios.find((elem) => elem.agent_id === ag.agent.id);
                        if (findCurrentAgent) {
                            return {
                                ...ag,
                                ticket_payment_ratio: findCurrentAgent.ticket_payment_ratio
                            }
                        }
                        return ag;
                    })
                }

            };
            if (isLoginUserIsSP && data?.agents?.length > 0 && data?.milestones?.length > 0) {
                const findMilestonesOfCurrentUserAccepted = data.agents.filter((ag) => ag.agent.id === newLoggedUserData.user.id && ag.role === 'SP').map((elem) => elem.milestone_id) || [];
                data.milestones = data.milestones.filter((milestone) => findMilestonesOfCurrentUserAccepted.includes(milestone.id));
            }

            if (data?.milestones?.length > 0) {
                const total_dist_travelled_km = data?.milestones?.reduce((acc, milestone) => {
                    if (parseFloat(milestone?.total_dist_travelled_km) > 0) {
                        acc += parseFloat(milestone?.total_dist_travelled_km);
                    }
                    return acc;
                }, 0) || 0;
                data.total_dist_travelled_km = total_dist_travelled_km;
            }
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_SUCCESS, payload: data });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        if (call_after_site_update) {
            yield put({ type: loading.GET_SERVICE_REQUEST_BY_ID_AFTER_SITE_UPDATE_LOADING, payload: false });
        } else {
            yield put({ type: loading.GET_SERVICE_REQUEST_BY_ID_LOADING, payload: false });
        }
    }
}

export function* createOrEditServiceRequestPayment(action) {
    const { payload, formName, ticketId, onSuccess = () => { } } = action.payload;

    try {
        if (payload.proof && payload.proof.length > 0) {
            try {
                const files = Array.from(payload.proof);
                const requestBody = {
                    files: files.map(file => ({
                        name: file?.name,
                        mime_type: file?.type,
                        size: file?.size,
                    })),
                    purpose: 'TICKET_ADDITIONAL_PAYMENT_PROOF',
                    ticket_id: ticketId,
                    milestone_id: payload.milestone_id
                };

                const uploadResponse = yield call(scogo_apis.user.postImageUrl, {}, {}, {}, requestBody);

                if (uploadResponse?.status === 201 && uploadResponse?.data?.data?.postUrls) {
                    const uploadedData = uploadResponse.data.data.postUrls;
                    yield Promise.all(
                        files.map(async (file, index) => {
                            try {
                                const f = uploadedData.find(({ name }) => name === file?.name);
                                if (!f) {
                                    console.log(`No signed URL found for file: ${file?.name || 'unknown'}`);
                                    return;
                                }

                                const body = {
                                    'Content-Type': f.mimeType || 'application/octet-stream',
                                    'key': f.payload?.key || '',
                                    ...(f.payload?.acl ? { 'acl': f.payload.acl } : {}),
                                    'X-Amz-Date': f.payload?.date || '',
                                    'X-Amz-Algorithm': f.payload?.algorithm || '',
                                    'x-amz-meta-uuid': f.payload?.uuid || '',
                                    'X-Amz-Credential': f.payload?.credential || '',
                                    'Policy': f.payload?.policy || '',
                                    'X-Amz-Signature': f.payload?.signature || '',
                                };

                                await uploadFileToS3WithSignedUrlNew(f.postUrl, file, body);
                            } catch (uploadError) {
                                console.log(`Error uploading file ${file?.name || 'unknown'}:`, uploadError);
                            }
                        })
                    );
                    payload.proof = uploadedData?.[0]?.fileUrl;
                };
            } catch (mediaError) {
                console.log('Error processing media files:', mediaError);
                payload.proof = '';
            }
        }

        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequestPayments.createOrEdit, {}, { ticketId }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Payment Created Successfully' } }),
                put({ type: serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_REQUEST, payload: { ticketId, payload: {} } }),
            ]);
            onSuccess && onSuccess()
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* approveServiceRequestPayment(action) {
    const { payload, formName, ticketId, milestone_id } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequestPayments.approve, {}, { ticketId }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Payment Approved Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_REQUEST, payload: { ticketId, payload: {} } })
            ]);
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* rejectServiceRequestPayment(action) {
    const { payload, formName, ticketId, milestone_id } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequestPayments.reject, {}, { ticketId }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Payment Rejected Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_REQUEST, payload: { ticketId, payload: {} } })
            ]);
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* rollbackServiceRequestPayment(action) {
    const { payload, formName, ticketId, milestone_id } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequestPayments.rollback, {}, { ticketId }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Payment Rolled Back Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_REQUEST, payload: { ticketId, payload: {} } })
            ]);
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* getServiceRequestPayments(action) {
    let { ticketId, payload, roles, org_id } = action.payload;
    try {
        const newLoggedUserData = yield select(state => state.auth.newLoggedUserData);
        let role;
        if (!roles) {
            const serviceRequestData = yield select(state => state.serviceRequest.serviceRequestData);
            if (serviceRequestData && serviceRequestData[ticketId]) {
                roles = serviceRequestData[ticketId].agents.filter(agent => agent.role !== 'CUSTOMER' && agent.agent.id === newLoggedUserData.user.id).map(agent => agent.role);
                org_id = serviceRequestData[ticketId].helpdesk_id;
            }
        }
        if (roles.includes('SCM')) role = 'SCM';
        else if (org_id === newLoggedUserData.org.id) role = 'CUSTOMER'
        else if (roles.includes('CLUSTER')) role = 'CLUSTER';
        else if (roles.includes('PSP')) role = 'PSP';
        else if (roles.includes('SUPERVISOR')) role = 'SUPERVISOR';
        else if (roles.includes('CUSTOMER')) role = 'CUSTOMER';
        else if (roles.includes('WAREHOUSE_OWNER')) role = 'WAREHOUSE_OWNER';
        else if (roles.includes('SP')) role = 'SP';
        yield put({ type: loading.SET_SERVICE_REQUEST_PAYMENTS_LOADING, payload: true });
        const response = yield call(scogo_apis.serviceRequestPayments.getPayments, {}, { ticketId, role }, { ...payload }, {});
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_SUCCESS, payload: { ticketId, data: response.data.data } });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.SET_SERVICE_REQUEST_PAYMENTS_LOADING, payload: false });
    }
}

export function* getServiceRequestMilestones(action) {
    const { id } = action.payload;
    try {
        const newLoggedUserData = yield select(state => state.auth.newLoggedUserData);
        const response = yield call(scogo_apis.serviceRequestPayments.getMilestones, {}, { ticketId: id }, { limit: 25, page: 1 }, {});
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_MILESTONES_SUCCESS, payload: { id, data: response.data.data, newLoggedUserData } });
        }
    } catch (error) {
        console.log('error:', error);
    }
}

export function* reachedSiteServiceRequest(action) {
    const { ticketId, milestoneId, formName, refresh } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.reachedSite, {}, { ticketId, milestoneId }, {}, {});
        if (response.status === 200) {
            yield all([
                put({ type: serviceRequest.REACHED_SITE_SERVICE_REQUEST_SUCCESS, payload: { milestone: response.data.data, ticketId } }),
                put({ type: serviceRequest.GET_SERVICE_REQUEST_MILESTONES_REQUEST, payload: { id: ticketId } }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Reached Site Successfully' } }),
            ]);

            if (refresh) {
                yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
            }
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* approveTicketMilestone(action) {
    const { ticketId, milestoneId, payload, formName, onSuccess = () => { } } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.approve, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Ticket Milestone Approved Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
            onSuccess();
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* rejectTicketMilestone(action) {
    const { ticketId, milestoneId, payload, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.reject, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Ticket Milestone Rejected Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* raiseSignoffTicketMilestone(action) {
    const { ticketId, milestoneId, payload, formName, closeModal, onSuccess } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.milestones.raiseSignoff, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            if (closeModal === false) {
                yield all([
                    put({ type: serviceRequest.GET_SERVICE_REQUEST_MILESTONES_REQUEST, payload: { id: ticketId } }),
                    put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Ticket Milestone Raised Signoff Successfully' } }),
                    put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId } }),
                ]);
                if (typeof onSuccess === 'function') onSuccess(response.data);
            } else {
                // Preserve the existing Raise Signoff lifecycle for every non-embedded caller.
                yield all([
                    put({ type: serviceRequest.GET_SERVICE_REQUEST_MILESTONES_REQUEST, payload: { id: ticketId } }),
                    put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Ticket Milestone Raised Signoff Successfully' } }),
                    put({ type: modal.CLOSE_MODAL }),
                    put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId } }),
                ]);
            }
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* updateServiceRequestTask(action) {
    const { ticketId, taskId, payload, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(iceberg_apis.task.update, {}, { ticketId, taskId }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: serviceRequest.GET_SERVICE_REQUEST_MILESTONES_REQUEST, payload: { id: ticketId } }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Task Updated Successfully' } }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
};


function* fetchTicketPaymentsCount(queryParams) {
    try {
        const callCountApiResponse = yield call(scogo_apis.serviceRequest.getAllTicketPaymentsCount, {}, {}, queryParams, {});
        if (callCountApiResponse && callCountApiResponse.status === 200 && callCountApiResponse.data && callCountApiResponse.data.data) {
            yield put({ type: serviceRequest.SET_PAYMENT_COUNT, payload: callCountApiResponse.data.data });
        }
    } catch (error) {
        console.log('error fetching payment count:', error);
    }
}

export function* getAllTicketPayment(action) {
    const { limit, page, query, payload } = action.payload;
    let queryParams = { limit, page };
    if (query) queryParams.q = query;
    if (payload) queryParams = { ...queryParams, ...payload };

    Object.keys(RegionsType).forEach(key => {
        delete queryParams[key];
    });

    yield put({ type: serviceRequest.SET_PAYMENT_FILTER_PAYLOAD, payload: action.payload });

    try {
        yield put({ type: loading.SET_ALL_TICKET_PAYMENTS_LOADING, payload: true });
        let response;
        if (Object.keys(payload).length === 1 && payload.ticket_status) {
            response = yield call(scogo_apis.serviceRequestPayments.getAllTicketaPayments, {}, {}, queryParams, {});
        } else {
            response = yield call(scogo_apis.serviceRequestPayments.findAllTicketaPayments, {}, {}, {}, queryParams);
        }

        if (page === 1 && response.data) yield fork(fetchTicketPaymentsCount, queryParams);
        else if (response.data && response.data.metadata) {
            const paymentCount = yield select(state => state.serviceRequest.paymentCount);
            if (paymentCount) {
                response.data.metadata.total_count = paymentCount;
            }
        }

        if (response && response.status === 200) {
            yield put({ type: serviceRequest.GET_ALL_TICKET_PAYMENTS_SUCCESS, payload: response.data });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.SET_ALL_TICKET_PAYMENTS_LOADING, payload: false });
    }
};


export function* updateRma(action) {
    const { payload, formName, source } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.updateRma, {}, {}, {}, payload);
        if (response.status === 201) {
            const arrayOfActions = [
                put({ type: modal.CLOSE_MODAL }),
                put({ type: modal.CLOSE_FULL_SCREEN_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'RMA Updated Successfully' } }),

            ]
            if (source === 'edit_payout') {
                arrayOfActions.push(put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId: payload.ticket_id } }));
            } else {
                arrayOfActions.push(put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }));
            }
            yield all(arrayOfActions);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* mapFaultyAsset(action) {
    const { payload, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.inventory.mapFaultyAsset, {}, {}, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Faulty Asset Mapped Successfully' } }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* triggeredServiceRequest(action) {
    const { payload, limit = 100, page = 1, query, user_id } = action.payload;
    const queryParams = { limit, page, };
    if (query) queryParams.q = query;
    if (user_id) queryParams.user_id = user_id;
    try {
        if (page > 1) {
            yield put({ type: loading.SET_NEXT_PAGE_TRIGGERED_SERVICE_REQUEST_LOADING, payload: true });
        } else {
            yield put({ type: loading.SET_TRIGGERED_SERVICE_REQUEST_LOADING, payload: true });
        }
        const response = yield call(scogo_apis.serviceRequest.triggeredServiceRequest, {}, {}, queryParams, payload);
        if (response.status === 200) {
            yield put({ type: serviceRequest.TRIGGERED_SERVICE_REQUEST_SUCCESS, payload: { data: response.data.data, page } });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        if (page > 1) {
            yield put({ type: loading.SET_NEXT_PAGE_TRIGGERED_SERVICE_REQUEST_LOADING, payload: false });
        } else {
            yield put({ type: loading.SET_TRIGGERED_SERVICE_REQUEST_LOADING, payload: false });
        }
    }
}


export function* getEligibleEngineers(action) {
    const { user_id, limit, page, query, payload } = action.payload;
    try {
        const queryParams = {};
        if (limit) queryParams.limit = limit;
        if (page) queryParams.page = page;
        if (query) queryParams.q = query;
        yield put({ type: loading.SET_ELIGIBLE_ENGINEERS_LOADING, payload: true });
        const response = yield call(scogo_apis.user.workers, {}, { userId: user_id }, queryParams, payload);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_ELIGIBLE_ENGINEERS_SUCCESS, payload: response.data.data });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.SET_ELIGIBLE_ENGINEERS_LOADING, payload: false });
    }
}


export function* updateRemark(action) {
    const { ticketId, ledgerId, payload, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.updateRemark, {}, { ticketId, ledgerId }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Remark Updated Successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST })
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* addAttributes(action) {
    const { ticketId, key, payload, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.addAttributes, {}, { ticketId, key }, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Attributes Added Successfully' } }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* getExtraDetailsByIds(action) {
    const { ids } = action.payload;
    try {
        const newLoggedUserData = yield select((state) => state.auth.newLoggedUserData);
        const response = yield call(scogo_apis.serviceRequest.extraDetailsByIds, {}, {}, { ids }, {});
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_EXTRA_DETAILS_BY_IDS_SUCCESS, payload: { additonalData: response.data, newLoggedUserData } });
        }
    } catch (error) {
        console.log('error:', error);
    }
}


export function* cloneServiceRequest(action) {
    const { id, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.cloneTicket, {}, { ticketId: id }, {}, {});
        if (response.status === 201) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Ticket Cloned Successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* uploadTaskAndUpdate(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    const newLoggedUserData = yield select(state => state.auth.newLoggedUserData);
    try {
        const files = Array.from(payload.files);
        const requestBody = {
            files: files.map(file => ({
                name: file.name,
                mime_type: file.type,
                size: file.size
            })),
            purpose: 'TICKET_TASK',
            task_id: payload.task_id,
            ticket_id: payload.ticket_id,
        };

        const uploadResponse = yield call(scogo_apis.user.postImageUrl, {}, {}, {}, requestBody);
        if (uploadResponse.status !== 201) throw new Error('Failed to get signed URL');

        const uploadedData = uploadResponse?.data?.data?.postUrls;

        yield Promise.all(
            files.map(async (file, index) => {
                const f = uploadedData.find(({ name }) => name === file.name);
                if (!f) {
                    console.error(`No signed URL found for file: ${file.name}`);
                    return;
                }

                const body = {
                    'Content-Type': f.mimeType,
                    'key': f.payload.key,
                    ...(f.payload.acl ? { 'acl': f.payload.acl } : {}),
                    'X-Amz-Date': f.payload.date,
                    'X-Amz-Algorithm': f.payload.algorithm,
                    'x-amz-meta-uuid': f.payload.uuid,
                    'X-Amz-Credential': f.payload.credential,
                    'Policy': f.payload.policy,
                    'X-Amz-Signature': f.payload.signature,
                };

                try {
                    await uploadFileToS3WithSignedUrlNew(f.postUrl, file, body);
                } catch (uploadError) {
                    console.error(`Error uploading file ${file.name}:`, uploadError);
                }
            })
        );
        yield put({
            type: serviceRequest.SERVICE_REQUEST_TASK_UPDATE_REQUEST, payload: {
                ticketId: payload.ticket_id, taskId: payload.task_id, payload: {
                    responses: [],
                    files: uploadedData.map((elem, index) => {
                        return {
                            url: elem.fileUrl,
                            name: elem.name,
                            mime_type: elem.mimeType,
                            size: requestBody.files[index].size
                        }
                    }),
                    response_by: newLoggedUserData.user.id,
                }, formName
            }
        });

    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* releaseVendor(action) {
    const { formName, ticketId, milestoneId, closeModal, onSuccess, refresh = true } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.milestones.releaseVendor, {}, { ticketId, milestoneId }, {});
        if (response.status === 201) {
            if (onSuccess) onSuccess();
            if (closeModal) yield put({ type: modal.CLOSE_MODAL });
            if (refresh) yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
            yield put({
                type: toast.SEND_TOAST, payload: {
                    status: 'success',
                    message: response.data.message || 'Vendor released successfully!!!'
                }
            })
            yield
        }
    } catch (err) {
        yield put({
            type: toast.SEND_TOAST, payload: {
                status: 'danger',
                message: err.response.data.messages[0] || 'Something Went wrong'
            }
        })
        console.log('errro')
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* addBoqInServiceRequest(action) {
    const { formName, payload, onSuccess = () => { }, onError, closeModal = false, refresh, showToast, pavFlow } = action.payload;
    const isPavCustomFlow = pavFlow === true;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.inventory.createBoq, {}, {}, {}, payload);
        if (response.status === 201 && ![400, 404].includes(response?.data?.code) && !response?.data?.exception) {
            if (isPavCustomFlow) {
                if (refresh !== false) yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId: payload.ticket_id } });
                onSuccess(response?.data);
                if (showToast !== false) yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response?.data?.message || 'Boq added successfully' } });
            } else {
                // Preserve the existing Add BOQ lifecycle for every current caller.
                yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId: payload.ticket_id } });
                onSuccess();
                yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response?.data?.message || 'Boq added successfully' } });
            }
            if (closeModal) yield put({ type: modal.CLOSE_MODAL });
        } else {
            const errorMessage = response?.data?.messages?.[0] || 'Something went wrong';
            if (!isPavCustomFlow || showToast !== false) yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: errorMessage } });
            if (isPavCustomFlow && typeof onError === 'function') onError(new Error(errorMessage));
        }
    } catch (boqError) {
        console.error('Error creating BOQ:', boqError);
        const errorMessage = boqError?.response?.data?.messages?.[0] || 'Ticket created but there was an issue with BOQ items';
        if (!isPavCustomFlow || showToast !== false) yield put({ type: toast.SEND_TOAST, payload: { status: 'warning', message: errorMessage } });
        if (isPavCustomFlow && typeof onError === 'function') onError(boqError);
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* goingToSiteServiceRequest(action) {
    const { ticketId, milestoneId, formName, refresh } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.milestones.goingToSite, {}, { ticketId, milestoneId }, {});
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Going to site requested successfully' } });
            yield put({ type: serviceRequest.GOING_TO_SITE_SERVICE_REQUEST_SUCCESS, payload: { milestone: response.data.data, ticketId } });
        }
        if (refresh) {
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* addDocketDetails(action) {
    const { ticketId, milestoneId, payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(iceberg_apis.milestones.addDocketDetails, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Docket details added successfully' } });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* getServiceRequestHistory(action) {
    const { ticketId } = action.payload;
    try {
        yield put({ type: loading.SET_SERVICE_REQUEST_HISTORY_LOADING, payload: true });
        const response = yield call(scogo_apis.serviceRequest.history, {}, { ticketId }, {});
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_HISTORY_SUCCESS, payload: { ticketId, data: response.data } });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.SET_SERVICE_REQUEST_HISTORY_LOADING, payload: false });
    }

}


export function* addMilestoneInServiceRequest(action) {
    const { ticketId, payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.milestones.addMilestone, {}, { ticketId }, {}, payload);
        if (response.status === 201) {
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Milestone added successfully' } });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* bulkServiceRequestStateChange(action) {
    const { payload, state, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkHoldOrResumeServiceRequest, {}, { state }, {}, payload);
        if (response.status === 200) {
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Service request state changed successfully' } });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* bulkCloneServiceRequest(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkCloneTicket, {}, {}, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Service request cloned successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* bulkDeleteServiceRequest(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkDeleteTicket, {}, {}, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Service request deleted successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* bulkEditExecutionDateOfServiceRequest(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkEditExecutionDate, {}, {}, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Execution date updated successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* bulkAssignAgentsOfServiceRequest(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkAssign, {}, {}, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: modal.CLOSE_MODAL }),
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Agents assigned successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* addServiceRequestRemark(action) {
    const { ticketId, payload, formName, closeModal, callHistory } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.addRemark, {}, { ticketId }, {}, payload);
        if (response.status === 201) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Remark added successfully' } }),
                put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST }),
            ]);

            if (closeModal) yield put({ type: modal.CLOSE_MODAL });
            if (callHistory) yield put({ type: serviceRequest.GET_SERVICE_REQUEST_HISTORY_REQUEST, payload: { ticketId } });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* unlockSignoffServiceRequest(action) {
    const { ticketId, milestoneId, formName, remark } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.milestones.unlockSignoff, {}, { ticketId, milestoneId }, {}, { remark });
        if (response.status === 200) {
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Signoff unlocked successfully' } });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* bulkRemoveAgent(action) {
    const { ticket_ids, roles, formName } = action.payload
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.bulkRemoveAgent, {}, {}, {}, { ticket_ids, roles })
        if (response.status === 201) {
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    }
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
}


export function* generateSignoffReport(action) {
    const { ticketId, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.signoffReport, {}, { ticketId }, {});
        if (response.status === 200) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Report generated successfully' } });
            if (response?.data?.downloads?.length > 0) {
                window.open(response?.data?.downloads[0], '_blank');
            }
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* generateTicketReport(action) {
    const { ticketId, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.report, {}, { ticketId }, {});
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Report generated successfully' } });
            if (response?.data?.data?.fileUrl) {
                window.open(response.data.data.fileUrl, '_blank');
            }
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* updateNoResponse(action) {
    const { ticketId, milestoneId, event, formName } = action.payload;
    try {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
        const response = yield call(scogo_apis.serviceRequest.updateNoResponse, {}, { ticketId, milestoneId, event }, {}, {});
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'No response updated successfully' } });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* getSupportTrackTicketDetails(action) {
    const { ticketId } = action.payload;
    try {
        yield put({ type: loading.SET_SUPPORT_TRACK_SERVICE_REQUEST_LOADING, payload: true });
        const response = yield call(scogo_apis.webhooks.ticketDetails, {}, { ticketId }, {});
        if (response.status === 200) {
            yield put({ type: serviceRequest.SUPPORT_TRACK_TICKET_DETAILS_SUCCESS, payload: { data: response.data, error: false } });
        } else {
            yield put({ type: serviceRequest.SUPPORT_TRACK_TICKET_DETAILS_SUCCESS, payload: { data: {}, error: true } });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: serviceRequest.SUPPORT_TRACK_TICKET_DETAILS_SUCCESS, payload: { data: {}, error: true } });
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.SET_SUPPORT_TRACK_SERVICE_REQUEST_LOADING, payload: false });
    }

}


export function* getServiceRequestCountV2(action) {
    const data = action.payload;
    if (Object.keys(data).length > 0) data.is_filtered = true;
    try {
        const response = yield call(scogo_apis.serviceRequest.count, {}, {}, data);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_COUNT_V2_SUCCESS, payload: response.data.data });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    }
}

export function* bulkBroadcast(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkBroadcast, {}, {}, {}, payload);
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Bulk Broadcasted Successfully!!!' } });
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });

        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* downloadCustomerSignoffReport(action) {
    const { payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.downloadCustomerSignoffReport, {}, {}, {}, payload);
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message } });
            yield put({ type: modal.CLOSE_MODAL });
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

// Uploads the customer approval proofs to S3 and returns the `attachments` array the approval API expects.
// The batch is anchored to its first ticket because the media service scopes ticket uploads to a ticket id.
function* uploadBillingApprovalAttachments(files, ticketId) {
    const filesToUpload = Array.from(files || []);
    if (filesToUpload.length === 0) return [];

    const requestBody = {
        files: filesToUpload.map((file) => ({ name: file.name, mime_type: file.type, size: file.size })),
        purpose: 'TICKET_CUSTOM',
        ticket_id: ticketId,
    };

    const signedUrlResponse = yield call(scogo_apis.user.postImageUrl, {}, {}, {}, requestBody);
    if (signedUrlResponse.status !== 201) throw new Error('Failed to upload attachments');
    const postUrls = signedUrlResponse?.data?.data?.postUrls || [];

    yield Promise.all(
        filesToUpload.map(async (file) => {
            const f = postUrls.find(({ name }) => name === file.name);
            if (!f) {
                console.error(`No signed URL found for file: ${file.name}`);
                return;
            }
            const body = {
                'Content-Type': f.mimeType,
                'key': f.payload.key,
                ...(f.payload.acl ? { 'acl': f.payload.acl } : {}),
                'X-Amz-Date': f.payload.date,
                'X-Amz-Algorithm': f.payload.algorithm,
                'x-amz-meta-uuid': f.payload.uuid,
                'X-Amz-Credential': f.payload.credential,
                'Policy': f.payload.policy,
                'X-Amz-Signature': f.payload.signature,
            };
            await uploadFileToS3WithSignedUrlNew(f.postUrl, file, body);
        })
    );

    return postUrls
        .map((f) => {
            const file = filesToUpload.find(({ name }) => name === f.name);
            if (!file || !f.fileUrl) return undefined;
            return { url: f.fileUrl, name: f.name, mime_type: f.mimeType, size: file.size, ...(f.id ? { media_id: f.id } : {}) };
        })
        .filter(Boolean);
}

// The mark and revoke endpoints answer with a partial-success batch, so surface the tickets that were refused.
function* handleBillingApprovalResponse(response, { onSuccess } = {}) {
    const { marked, failedTickets = [] } = response.data?.data || {};
    const status = marked > 0 ? 'success' : 'warning';
    yield put({ type: toast.SEND_TOAST, payload: { status, message: response.data.message } });

    if (failedTickets.length > 0) {
        const reasons = failedTickets.slice(0, 5).map(({ ticket_id, error }) => `${ticket_id}: ${error}`).join(', ');
        const remaining = failedTickets.length > 5 ? ` and ${failedTickets.length - 5} more` : '';
        yield put({ type: toast.SEND_TOAST, payload: { status: 'warning', message: `${reasons}${remaining}` } });
    }

    yield put({ type: modal.CLOSE_MODAL });
    yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
    if (typeof onSuccess === 'function') onSuccess(response.data?.data);
}

export function* customerApprovalRecievedRequest(action) {
    const { payload, files, formName, onSuccess } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const attachments = yield call(uploadBillingApprovalAttachments, files, payload.ticket_ids?.[0]);
        const requestPayload = { ...payload, ...(attachments.length > 0 ? { attachments } : {}) };
        const response = yield call(scogo_apis.serviceRequest.customerApprovalRecieved, {}, {}, {}, requestPayload);
        if (response.status === 201 || response.status === 200) {
            yield call(handleBillingApprovalResponse, response, { onSuccess });
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || error?.message || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* revokeCustomerApprovalRequest(action) {
    const { payload, formName, onSuccess } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.revokeCustomerApproval, {}, {}, {}, payload);
        if (response.status === 201 || response.status === 200) {
            yield call(handleBillingApprovalResponse, response, { onSuccess });
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* getBillingRemarks(action) {
    const { limit, page, query, payload } = action.payload;
    yield put({ type: loading.SET_BILLING_REMARKS_LOADING, payload: true });
    // A refresh always restarts from the first page, so that is what gets remembered.
    yield put({ type: serviceRequest.SET_BILLING_REMARKS_FILTER_PAYLOAD, payload: { limit, page: 1, query, payload } });
    try {
        const queryParams = { limit, page, get_count: true, sort_order: 'desc', ...payload };
        if (query) queryParams.q = query.trim();
        const response = yield call(scogo_apis.serviceRequest.getBillingRemarks, {}, {}, queryParams);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_BILLING_REMARKS_SUCCESS, payload: { ...response.data, page } });
        }
    } catch (error) {
        yield put({ type: serviceRequest.GET_BILLING_REMARKS_FAILURE });
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.SET_BILLING_REMARKS_LOADING, payload: false });
    }
}

export function* refreshBillingRemarks() {
    try {
        const billingRemarksFilterPayload = yield select((state) => state.serviceRequest.billingRemarksFilterPayload);
        // Nothing to replay until the remarks list has been opened at least once.
        if (!billingRemarksFilterPayload) return;
        yield put({ type: serviceRequest.GET_BILLING_REMARKS_REQUEST, payload: billingRemarksFilterPayload });
    } catch (error) {
        console.log('error:', error);
    }
}

export function* editCustomerRequest(action) {
    const { id, formName, media_ids } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.editCustomer, {}, { id }, {}, { media_ids });
        if (response.status === 200) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: 'Report Updated Successfully!!!' } });
            const url = response.data?.data?.fileUrl;
            if (url) {
                window.open(url, '_blank'); // Will open the link in a new tab
            }

            yield put({ type: modal.CLOSE_MODAL });
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* bulkSignoffUnlockAction(action) {
    const { formName, milestone_ids, remark } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.bulkSignoffUnlock, {}, {}, {}, { milestone_ids, remark });
        if (response.status === 201) {
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Signoff unlocked successfully' } });
        }
    } catch (error) {
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* holdServiceRequestPayment(action) {
    const { payload, formName, ticketId, milestone_id } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequestPayments.hold, {}, { ticketId, milestone_id }, {}, payload);
        if (response.status === 200) {
            yield all([
                put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Payment Rolled Back Successfully' } }),
                put({ type: modal.CLOSE_MODAL }),
                put({ type: serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_REQUEST, payload: { ticketId, payload: {} } })
            ]);
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}


export function* removeAssetFromTicket(action) {
    const { id, asset_id, formName, onSuccess } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.removeAssetFromTicket, {}, { asset_id }, {}, {
            ticket_id: id
        });
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Asset removed from ticket successfully' } });
            yield put({ type: serviceRequest.REMOVE_ASSET_FROM_TICKET_SUCCESS, payload: { ticketId: id, asset_id: asset_id } });
            if (onSuccess) onSuccess();
        }

    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* fixRma(action) {
    const { id, milestoneId, formName, payload } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.serviceRequest.fixRma, {}, { id, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'RMA fixed successfully' } });
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId: id } });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* verifyPavAssetPhysically(action) {
    const { ticketId, milestoneId, assetId, isVerified, remark, formName, onSuccess, onError, refresh } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const payload = { is_verified: isVerified === true };
        if (typeof remark === 'string' && remark.trim()) payload.remark = remark.trim();
        const response = yield call(scogo_apis.milestones.assetPhysicalVerification, {}, { ticketId, milestoneId, assetId }, {}, payload);
        if ([200, 201].includes(response.status) && ![400, 404].includes(response?.data?.code) && !response?.data?.exception) {
            const message = response?.data?.message || (isVerified ? 'Asset verified successfully' : 'Asset rejected successfully');
            // Remembered outside the ticket cache so the decision survives a
            // ticket refresh that has not picked the new flag up yet, and any
            // remount of the verification screens.
            yield put({ type: serviceRequest.PAV_ASSET_PHYSICAL_VERIFICATION_SUCCESS, payload: { ticketId, milestoneId, assetId, isVerified: isVerified === true } });
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message } });
            if (refresh !== false) yield put({ type: serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, payload: { ticketId, callDetail: true } });
            if (typeof onSuccess === 'function') onSuccess(response?.data);
        } else {
            const errorMessage = response?.data?.messages?.[0] || response?.data?.message || 'Something went wrong';
            yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: errorMessage } });
            if (typeof onError === 'function') onError(new Error(errorMessage));
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.messages?.[0] || error?.response?.data?.message || 'Something went wrong';
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: errorMessage } });
        if (typeof onError === 'function') onError(error);
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* getServiceRequestCountByStatus(action) {
    const queryParams = action.payload;
    try {
        const response = yield call(scogo_apis.serviceRequest.getServiceRequestCountByStatus, {}, {}, queryParams);
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_SERVICE_REQUEST_COUNT_BY_STATUS_SUCCESS, payload: response.data.data.counts });
        }
    } catch (error) {
        console.log('error:', error);
        // yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    }
};


export function* updateServiceRequestCustomerSignoffStatus(action) {
    const { ticketId, milestoneId, payload, formName } = action.payload;
    yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: true } });
    try {
        const response = yield call(scogo_apis.milestones.updateCustomerSignoffStatus, {}, { ticketId, milestoneId }, {}, payload);
        if (response.status === 201) {
            yield put({ type: toast.SEND_TOAST, payload: { status: 'success', message: response.data.message || 'Customer signoff status updated successfully' } });
            yield put({ type: modal.CLOSE_MODAL });
            yield put({ type: serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST });
        }
    } catch (error) {
        console.log('error:', error);
    } finally {
        yield put({ type: loading.FORM_BUTTON_LOADING, payload: { formName, isLoading: false } });
    }
}

export function* getVisitHistory(action) {
    const { ticketId } = action.payload;
    try {
        yield put({ type: loading.SET_VISIT_HISTORY_LOADING, payload: true });
        const response = yield call(scogo_apis.serviceRequest.visitHistory, {}, { ticketId }, {});
        if (response.status === 200) {
            yield put({ type: serviceRequest.GET_VISIT_HISTORY_SUCCESS, payload: { ticketId, data: response.data.data } });
        }
    } catch (error) {
        console.log('error:', error);
        yield put({ type: toast.SEND_TOAST, payload: { status: 'danger', message: error?.response?.data?.messages?.[0] || 'Something went wrong' } });
    } finally {
        yield put({ type: loading.SET_VISIT_HISTORY_LOADING, payload: false });
    }
}

export default function* serviceRequestSaga() {
    yield takeLatest(serviceRequest.GET_SERVICE_REQUEST_LIST_REQUEST, getServiceRequestList);
    yield takeEvery(serviceRequest.CREATE_SERVICE_REQUEST_REQUEST, createServiceRequest);
    yield takeEvery(serviceRequest.UPDATE_SERVICE_REQUEST_REQUEST, updateServiceRequest);
    yield takeEvery(serviceRequest.DELETE_SERVICE_REQUEST_REQUEST, deleteServiceRequest);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_FILTER_LIST_REQUEST, getServiceRequestFilterList);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_COUNT_REQUEST, getServiceRequestCount);
    yield takeEvery(serviceRequest.REFRESH_SERVICE_REQUEST_REQUEST, refreshServiceRequestTable);
    yield takeEvery(serviceRequest.HOLD_OR_RESUME_SERVICE_REQUEST_REQUEST, holdOrResumeServiceRequest);
    yield takeEvery(serviceRequest.HARD_CLOSE_SERVICE_REQUEST_REQUEST, hardCloseServiceRequest);
    yield takeEvery(serviceRequest.BULK_TICKET_CREATE_REQUEST, bulkTicketCreate);
    yield takeEvery(serviceRequest.BULK_TICKET_EXAMPLE_FILE_REQUEST, bulkTicketExampleFile);
    yield takeEvery(serviceRequest.GET_ELIGIBLE_AGENTS_REQUEST, getEligibleAgents);
    yield takeEvery(serviceRequest.ACCEPT_MILESTONE_REQUEST, acceptTicketMilestone);
    yield takeEvery(serviceRequest.ASSIGN_AGENTS_REQUEST, assignAgentsToTicketMilestone);
    yield takeEvery(serviceRequest.BROADCAST_REQUEST, broadcastTicketMilestone);
    yield takeEvery(serviceRequest.SITE_COMPLETE_REQUEST, siteCompleteTicketMilestone);
    yield takeEvery(serviceRequest.EXECUTION_DATE_REQUEST, executionDateTicketMilestone);
    yield takeEvery(serviceRequest.REASSIGN_AGENTS_REQUEST, reAssignAgentToServiceReuest);
    yield takeEvery(serviceRequest.ASSIGN_HELPDESK_REQUEST, assignHelpdesk);
    yield takeLatest(serviceRequest.GET_SERVICE_REQUEST_BY_ID_REQUEST, getServiceRequestById);
    yield takeLatest(serviceRequest.SERVICE_REQUEST_PAYMENT_CREATE_OR_EDIT_REQUEST, createOrEditServiceRequestPayment);
    yield takeEvery(serviceRequest.SERVICE_REQUEST_PAYMENT_APPROVE_REQUEST, approveServiceRequestPayment);
    yield takeEvery(serviceRequest.SERVICE_REQUEST_PAYMENT_REJECT_REQUEST, rejectServiceRequestPayment);
    yield takeEvery(serviceRequest.SERVICE_REQUEST_PAYMENT_ROLLBACK_REQUEST, rollbackServiceRequestPayment);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_REQUEST, getServiceRequestPayments);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_MILESTONES_REQUEST, getServiceRequestMilestones);
    yield takeEvery(serviceRequest.REACHED_SITE_SERVICE_REQUEST_REQUEST, reachedSiteServiceRequest);
    yield takeEvery(serviceRequest.APPROVE_TICKET_MILESTONE_REQUEST, approveTicketMilestone);
    yield takeEvery(serviceRequest.REJECT_TICKET_MILESTONE_REQUEST, rejectTicketMilestone);
    yield takeEvery(serviceRequest.TICKET_RAISE_SIGNOFF_REQUEST, raiseSignoffTicketMilestone);
    yield takeEvery(serviceRequest.SERVICE_REQUEST_TASK_UPDATE_REQUEST, updateServiceRequestTask);
    yield takeLatest(serviceRequest.GET_ALL_TICKET_PAYMENTS_REQUEST, getAllTicketPayment);
    yield takeEvery(serviceRequest.UPDATE_RMA_REQUEST, updateRma);
    yield takeEvery(serviceRequest.MAP_FAULTY_ASSET_REQUEST, mapFaultyAsset);
    yield takeLatest(serviceRequest.TRIGGERED_SERVICE_REQUEST_REQUEST, triggeredServiceRequest);
    yield takeEvery(serviceRequest.GET_ELIGIBLE_ENGINEERS_REQUEST, getEligibleEngineers);
    yield takeEvery(serviceRequest.UPDATE_REMARK_REQUEST, updateRemark);
    yield takeEvery(serviceRequest.ADD_ATTRIBUTES_REQUEST, addAttributes);
    yield takeEvery(serviceRequest.GET_EXTRA_DETAILS_BY_IDS_REQUEST, getExtraDetailsByIds);
    yield takeEvery(serviceRequest.CLONE_SERVICE_REQUEST_REQUEST, cloneServiceRequest);
    yield takeEvery(serviceRequest.UPLOAD_TASKS_AND_UPDATE, uploadTaskAndUpdate);
    yield takeEvery(serviceRequest.RELEASE_SERVICE_REQUEST, releaseVendor);
    yield takeEvery(serviceRequest.ADD_BOQ_IN_SERVICE_REQUEST, addBoqInServiceRequest);
    yield takeEvery(serviceRequest.GOING_TO_SITE_SERVICE_REQUEST_REQUEST, goingToSiteServiceRequest);
    yield takeEvery(serviceRequest.ADD_DOCKET_DETAILS_REQUEST, addDocketDetails);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_HISTORY_REQUEST, getServiceRequestHistory);
    yield takeEvery(serviceRequest.ADD_MILESTONE_IN_SERVICE_REQUEST, addMilestoneInServiceRequest);
    yield takeEvery(serviceRequest.BULK_SERVICE_REQUEST_STATE_CHANGE_REQUEST, bulkServiceRequestStateChange);
    yield takeEvery(serviceRequest.BULK_CLONE_SERVICE_REQUEST_REQUEST, bulkCloneServiceRequest);
    yield takeEvery(serviceRequest.BULK_DELETE_SERVICE_REQUEST_REQUEST, bulkDeleteServiceRequest);
    yield takeEvery(serviceRequest.BULK_EDIT_EXECUTION_DATE_OF_SERVICE_REQUEST_REQUEST, bulkEditExecutionDateOfServiceRequest);
    yield takeEvery(serviceRequest.BULK_ASSIGN_AGENTS_OF_SERVICE_REQUEST_REQUEST, bulkAssignAgentsOfServiceRequest);
    yield takeEvery(serviceRequest.ADD_SERVICE_REQUEST_REMARK_REQUEST, addServiceRequestRemark);
    yield takeEvery(serviceRequest.UNLOCK_SIGN_OFF_SERVICE_REQUEST, unlockSignoffServiceRequest);
    yield takeEvery(serviceRequest.REMOVE_BULK_AGENTS_REQUEST, bulkRemoveAgent)
    yield takeEvery(serviceRequest.GENERATE_SIGN_OFF_REPORT_REQUEST, generateSignoffReport);
    yield takeEvery(serviceRequest.GENERATE_TICKET_REPORT_REQUEST, generateTicketReport);
    yield takeEvery(serviceRequest.UPDATE_NO_RESPONSE_REQUEST, updateNoResponse);
    yield takeEvery(serviceRequest.SUPPORT_TRACK_TICKET_DETAILS_REQUEST, getSupportTrackTicketDetails);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_COUNT_V2, getServiceRequestCountV2);
    yield takeEvery(serviceRequest.BULK_BROADCAST_REQUEST, bulkBroadcast);
    yield takeEvery(serviceRequest.DOWNLOAD_CUSTOMER_SIGNOFF_REPORT_REQUEST, downloadCustomerSignoffReport);
    yield takeEvery(serviceRequest.CUSTOMER_APPROVAL_RECEIVED_REQUEST, customerApprovalRecievedRequest);
    yield takeEvery(serviceRequest.REVOKE_CUSTOMER_APPROVAL_REQUEST, revokeCustomerApprovalRequest);
    yield takeLatest(serviceRequest.GET_BILLING_REMARKS_REQUEST, getBillingRemarks);
    yield takeLatest(serviceRequest.REFRESH_BILLING_REMARKS_REQUEST, refreshBillingRemarks);
    yield takeEvery(serviceRequest.EDIT_CUSTOMER_REPORT_REQUEST, editCustomerRequest);
    yield takeEvery(serviceRequest.BULK_SIGN_OFF_UNLOCK_REQUEST, bulkSignoffUnlockAction);
    yield takeEvery(serviceRequest.SERVICE_REQUEST_PAYMENT_HOLD_REQUEST, holdServiceRequestPayment);
    yield takeEvery(serviceRequest.REMOVE_ASSET_FROM_TICKET_REQUEST, removeAssetFromTicket);
    yield takeEvery(serviceRequest.FIX_RMA_REQUEST, fixRma);
    yield takeEvery(serviceRequest.PAV_ASSET_PHYSICAL_VERIFICATION_REQUEST, verifyPavAssetPhysically);
    yield takeEvery(serviceRequest.GET_SERVICE_REQUEST_COUNT_BY_STATUS_REQUEST, getServiceRequestCountByStatus);
    yield takeLatest(serviceRequest.UPDATE_CUSTOMER_SIGNOFF_STATUS_REQUEST, updateServiceRequestCustomerSignoffStatus);
    yield takeEvery(serviceRequest.GET_VISIT_HISTORY_REQUEST, getVisitHistory);
}
